/**
 * AddEvidenceModal Component
 * ORCA360 - Add Evidence from Transcript Modal
 *
 * Modal for selecting and adding evidence from transcript
 * Allows instructors to manually add evidence that AI may have missed
 *
 * Sprint 4, Day 3
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { normalizeTimestamp } from '../lib/timestampUtils';

export interface Evidence {
  quote: string;
  timestamp: string;
  validation_status: string;
  confidence?: number;
  // Tier 4 LLM Validation (optional - only present if LLM validation completed)
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  llm_reasoning?: string;
  reclassified_to_competency?: string;
}

export interface TranscriptSegment {
  id: string;
  speaker_label: string;
  start_ms: number;
  end_ms: number;
  text: string;
}

export interface AddEvidenceModalProps {
  sessionId: string;
  isOpen: boolean;
  onClose: () => void;
  onAdd: (evidence: Evidence) => void;
}

export function AddEvidenceModal({ sessionId, isOpen, onClose, onAdd }: AddEvidenceModalProps) {
  const [transcriptSegments, setTranscriptSegments] = useState<TranscriptSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSegments, setSelectedSegments] = useState<Set<string>>(new Set());
  const [manualQuote, setManualQuote] = useState('');
  const [manualTimestamp, setManualTimestamp] = useState('');
  const [mode, setMode] = useState<'select' | 'manual'>('select');
  const [searchQuery, setSearchQuery] = useState('');

  // Load transcript segments
  useEffect(() => {
    if (isOpen) {
      loadTranscriptSegments();
    }
  }, [isOpen, sessionId]);

  const loadTranscriptSegments = async () => {
    setLoading(true);
    try {
      // Get transcript ID for this session
      const { data: transcripts, error: transcriptError } = await supabase
        .from('transcripts')
        .select('id')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(1);

      if (transcriptError || !transcripts || transcripts.length === 0) {
        console.error('[AddEvidenceModal] No transcript found:', transcriptError);
        setTranscriptSegments([]);
        setLoading(false);
        return;
      }

      const transcript = transcripts[0];
      if (!transcript) {
        console.error('[AddEvidenceModal] No transcript found');
        setTranscriptSegments([]);
        setLoading(false);
        return;
      }

      const transcriptId = transcript.id;

      // Load speaker segments
      const { data: segments, error: segmentsError } = await supabase
        .from('speaker_segments')
        .select('*')
        .eq('transcript_id', transcriptId)
        .order('start_time', { ascending: true });

      if (segmentsError) {
        console.error('[AddEvidenceModal] Error loading segments:', segmentsError);
        setTranscriptSegments([]);
      } else {
        setTranscriptSegments(segments || []);
      }
    } catch (err) {
      console.error('[AddEvidenceModal] Error:', err);
      setTranscriptSegments([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSegmentToggle = (segmentId: string) => {
    const newSelected = new Set(selectedSegments);
    if (newSelected.has(segmentId)) {
      newSelected.delete(segmentId);
    } else {
      newSelected.add(segmentId);
    }
    setSelectedSegments(newSelected);
  };

  const handleAddFromSelection = () => {
    if (selectedSegments.size === 0) {
      alert('Please select at least one segment');
      return;
    }

    // Get selected segments
    const selected = transcriptSegments.filter((seg) => selectedSegments.has(seg.id));

    // Combine text
    const quote = selected.map((seg) => seg.text).join(' ');

    // Get time range
    const startTime = Math.min(...selected.map((seg) => seg.start_ms));
    const endTime = Math.max(...selected.map((seg) => seg.end_ms));
    const timestamp = `${formatTimestamp(startTime)}-${formatTimestamp(endTime)}`;

    // Add evidence (timestamp is already in HH:MM:SS format from formatTimestamp)
    onAdd({
      quote,
      timestamp,
      validation_status: 'VERIFIED', // Manually added = verified
      confidence: 1.0,
    });

    // Reset and close
    setSelectedSegments(new Set());
    onClose();
  };

  const handleAddManual = () => {
    if (!manualQuote.trim()) {
      alert('Please enter a quote');
      return;
    }

    if (!manualTimestamp.trim()) {
      alert('Please enter a timestamp');
      return;
    }

    // Normalize timestamp to HH:MM:SS format
    const normalizedTimestamp = normalizeTimestamp(manualTimestamp.trim());

    onAdd({
      quote: manualQuote.trim(),
      timestamp: normalizedTimestamp,
      validation_status: 'VERIFIED', // Manually added = verified
      confidence: 1.0,
    });

    // Reset and close
    setManualQuote('');
    setManualTimestamp('');
    onClose();
  };

  const filteredSegments = transcriptSegments.filter((seg) =>
    seg.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-steel/20">
          <h2 className="text-xl font-bold text-navy">Add Evidence</h2>
          <p className="text-sm text-steel mt-1">
            Select from transcript or enter manually
          </p>

          {/* Mode Toggle */}
          <div className="mt-4 flex space-x-2">
            <button
              onClick={() => setMode('select')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                mode === 'select'
                  ? 'bg-orca-elevate text-white'
                  : 'bg-steel/10 text-steel hover:bg-steel/20'
              }`}
            >
              Select from Transcript
            </button>
            <button
              onClick={() => setMode('manual')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                mode === 'manual'
                  ? 'bg-orca-elevate text-white'
                  : 'bg-steel/10 text-steel hover:bg-steel/20'
              }`}
            >
              Enter Manually
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {mode === 'select' ? (
            <>
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search transcript..."
                  className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
                />
              </div>

              {/* Transcript Segments */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
                  <p className="mt-2 text-steel">Loading transcript...</p>
                </div>
              ) : filteredSegments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-steel">No transcript segments found</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredSegments.map((segment) => (
                    <div
                      key={segment.id}
                      onClick={() => handleSegmentToggle(segment.id)}
                      className={`p-3 border rounded-md cursor-pointer transition-colors ${
                        selectedSegments.has(segment.id)
                          ? 'border-orca-elevate bg-orca-elevate/10'
                          : 'border-steel/30 hover:border-orca-elevate/50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-navy">{segment.text}</p>
                          <p className="text-xs text-steel mt-1">
                            {segment.speaker_label} • {formatTimestamp(segment.start_ms)} -{' '}
                            {formatTimestamp(segment.end_ms)}
                          </p>
                        </div>
                        {selectedSegments.has(segment.id) && (
                          <div className="ml-3 text-orca-elevate">✓</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedSegments.size > 0 && (
                <div className="mt-4 p-3 bg-orca-elevate/10 border border-orca-elevate/30 rounded-md">
                  <p className="text-sm text-navy font-semibold">
                    {selectedSegments.size} segment(s) selected
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Manual Entry */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Evidence Quote
                  </label>
                  <textarea
                    value={manualQuote}
                    onChange={(e) => setManualQuote(e.target.value)}
                    className="w-full h-32 px-3 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
                    placeholder="Enter evidence quote from your observations..."
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">
                    Timestamp
                  </label>
                  <input
                    type="text"
                    value={manualTimestamp}
                    onChange={(e) => setManualTimestamp(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md focus:outline-none focus:ring-2 focus:ring-orca-elevate text-sm"
                    placeholder="e.g., 00:03:45 or 00:03:45-00:04:30"
                  />
                  <p className="text-xs text-steel mt-1">
                    Format: HH:MM:SS or HH:MM:SS-HH:MM:SS for time range
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-steel/20 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-steel bg-steel/10 hover:bg-steel/20 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={mode === 'select' ? handleAddFromSelection : handleAddManual}
            disabled={mode === 'select' ? selectedSegments.size === 0 : !manualQuote.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors disabled:opacity-50"
          >
            Add Evidence
          </button>
        </div>
      </div>
    </div>
  );
}


